import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  Printer,
  X,
  Mail,
  Phone,
  MapPin,
  Globe,
  Hash,
  CalendarDays,
  Percent,
  AlertCircle,
} from "lucide-react";
import type { QuoteData } from "@/services/opportunityService";
import { useAuth } from "@/hooks/useAuth";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import logoKersales from "@/components/utils/Logo CRM.png";

interface QuoteViewerProps {
  data: QuoteData;
  onClose: () => void;
  onSendEmail?: () => Promise<{ success: boolean; message: string } | void>;
  isSending?: boolean;
}

export const QuoteViewer: React.FC<QuoteViewerProps> = ({
  data,
  onClose,
  onSendEmail,
  isSending = false,
}) => {
  const { company: authCompany } = useAuth();
  const branding = useSelector((state: RootState) => state.branding);
  const [imgError, setImgError] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [resultDialog, setResultDialog] = useState<{
    isOpen: boolean;
    type: "success" | "error";
    message: string;
  }>({ isOpen: false, type: "success", message: "" });

  useEffect(() => {
    if (!data || !data.quote) {
      onClose();
    }
  }, [data, onClose]);

  // Bloqueo de scroll y estilos de impresión
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const style = document.createElement("style");
    style.id = "print-isolation-styles-quote";
    style.innerHTML = `
      @media print {
        @page {
          margin: 10mm !important;
          size: letter portrait;
        }
        
        body {
          margin: 0 !important;
          padding: 0 !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          background: white !important;
        }

        body > *:not([data-quote-viewer="true"]) {
          display: none !important;
        }

        [data-quote-viewer="true"] {
          position: static !important;
          display: block !important;
          background: white !important;
        }

        .no-print { display: none !important; }

        .doc-container {
          box-shadow: none !important;
          border: none !important;
          width: 100% !important;
          max-width: none !important;
          padding: 0 !important;
        }
      }

      /* Barra de scroll neutra */
      .custom-scrollbar-quote::-webkit-scrollbar { width: 5px; }
      .custom-scrollbar-quote::-webkit-scrollbar-track { background: transparent; }
      .custom-scrollbar-quote::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
    `;
    document.head.appendChild(style);

    return () => {
      document.body.style.overflow = originalOverflow;
      const isolationStyle = document.getElementById(
        "print-isolation-styles-quote",
      );
      if (isolationStyle) isolationStyle.remove();
    };
  }, []);

  if (!data || !data.quote) return null;

  const handlePrint = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    window.print();
  };

  const {
    quote,
    company: quoteCompany,
    client,
    seller,
    items,
    totals,
    terms,
  } = data;

  // Usar company de auth si tiene branding, fallback a company de la data
  const displayCompany = {
    ...quoteCompany,
    logo_url: branding?.logo_url || (authCompany as any)?.logo_url || (quoteCompany as any)?.logo_url || logoKersales,
    primary_color:
      branding?.primary_color ||
      authCompany?.primary_color ||
      (quoteCompany as any).primary_color ||
      "#3b82f6",
    secondary_color:
      branding?.secondary_color ||
      authCompany?.secondary_color ||
      (quoteCompany as any).secondary_color ||
      "#1e1b4b",
    name: branding?.name || authCompany?.name || quoteCompany?.name || "Empresa",
    address: branding?.address || authCompany?.address || quoteCompany?.address,
    phone: branding?.phone || authCompany?.phone || quoteCompany?.phone,
    email: branding?.email || authCompany?.email || quoteCompany?.email,
    currency: branding?.currency || (authCompany as any)?.currency || (quoteCompany as any)?.currency || "Q",
  };

  const versionLabel =
    quote.minor_version > 0
      ? `v${quote.version_number}.${quote.minor_version}`
      : `v${quote.version_number}`;

  // Generate dates locally to avoid UTC timezone offsets from the server
  const displayDate = new Date().toLocaleDateString("es-ES", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const validityDateObj = new Date();
  validityDateObj.setDate(validityDateObj.getDate() + 15);
  const displayValidityDate = validityDateObj.toLocaleDateString("es-ES", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return createPortal(
    <div
      data-quote-viewer="true"
      className="fixed inset-0 z-[10000] bg-white flex flex-col pointer-events-auto overflow-hidden animate-in fade-in duration-200"
      onPointerDown={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
    >
      {/* Navbar Minimalista Blanca */}
      <div className="flex-shrink-0 bg-white border-b border-slate-100 px-8 py-2 flex items-center justify-between z-50 no-print shadow-sm">
        <div className="flex items-center gap-3">
          <Hash className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-slate-900 font-bold text-[11px]">
            Vista Previa #{quote.reference}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrint}
            className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-1.5 rounded-md font-bold text-[10px] transition-all flex items-center gap-2 border-none cursor-pointer"
          >
            <Printer className="w-3.5 h-3.5" />
            IMPRIMIR
          </button>
          {onSendEmail && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsConfirmOpen(true);
              }}
              className={`px-4 py-1.5 rounded-md font-bold text-[10px] transition-all flex items-center gap-2 border-none shadow-sm ${isSending || !client?.contact_email
                  ? "bg-slate-300 text-slate-500 cursor-not-allowed"
                  : "bg-slate-900 hover:bg-black text-white shadow-indigo-200 cursor-pointer"
                }`}
              disabled={isSending || !client?.contact_email}
              title={
                !client?.contact_email
                  ? "El contacto no tiene correo electrónico"
                  : ""
              }
            >
              <Mail className="w-3.5 h-3.5" />
              {isSending
                ? "ENVIANDO..."
                : !client?.contact_email
                  ? "SIN CORREO"
                  : "ENVIAR AL CLIENTE"}
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="w-8 h-8 hover:bg-slate-50 text-slate-400 rounded-lg flex items-center justify-center transition-all border-none cursor-pointer"
            title="Cerrar"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Area del Documento - Fondo Blanco */}
      <div className="flex-1 overflow-y-auto custom-scrollbar-quote p-4 md:p-8 bg-slate-50/30 print:bg-white print:p-0">
        <div className="max-w-[800px] mx-auto print:max-w-none">
          <div className="doc-container w-full bg-white shadow-sm print:shadow-none border border-slate-100 print:border-none flex flex-col p-8 md:p-12 print:p-0">
            <style>{`
              .doc-content { font-family: 'Inter', sans-serif; color: #334155; line-height: 1.4; font-size: 9pt; }
              .brand-primary { color: ${displayCompany.primary_color}; }
              .brand-secondary { color: #1e293b; } /* Color formal oscuro en lugar del color secundario brillante */
              .brand-primary-bg { background-color: ${displayCompany.primary_color}; }
              .brand-secondary-bg { background-color: #1e293b; }
              .brand-secondary-border { border-color: #1e293b; }
              .table-header-compact { background: #f1f5f9; border-bottom: 2px solid #1e293b; }
              .table-cell-compact { padding: 8px 10px; border-bottom: 1px solid #e2e8f0; }
            `}</style>

            <div className="doc-content">
              {/* HEADER COMPACTO */}
              <div className="flex justify-between items-start mb-6 pb-6 border-b border-slate-200">
                <div className="flex items-center gap-6">
                  <div className="w-48 h-20 flex items-center justify-start">
                    {displayCompany.logo_url && !imgError ? (
                      <img
                        src={displayCompany.logo_url}
                        alt={displayCompany.name}
                        className="max-w-full max-h-full object-contain object-left"
                        onError={() => setImgError(true)}
                      />
                    ) : (
                      <div className="font-black text-xl tracking-tighter brand-secondary">
                        {displayCompany.name.toUpperCase()}
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[7pt] font-black uppercase tracking-widest mb-1 text-slate-400">
                    Cotización Comercial
                  </div>
                  <div className="text-2xl font-black tracking-tighter leading-none mb-1 brand-secondary">
                    #{quote.reference}
                  </div>
                  <div className="flex items-center justify-end gap-2 text-slate-500 font-bold text-[7.5pt]">
                    <CalendarDays className="w-3 h-3" />
                    {displayDate}
                  </div>
                </div>
              </div>

              {/* INFO ORGANIZADA EN 3 COLUMNAS */}
              <div className="grid grid-cols-3 gap-6 mb-8 pb-6 border-b border-slate-200">
                <div className="col-span-1">
                  <div className="text-[7pt] font-black text-slate-400 uppercase tracking-widest mb-2">
                    Cliente
                  </div>
                  <div className="text-sm font-black leading-tight mb-1 brand-secondary">
                    {client?.company_name}
                  </div>
                  <div className="text-xs font-bold text-slate-600 mb-2">
                    {client?.contact_name}
                  </div>
                  <div className="text-[7.5pt] text-slate-500 space-y-1">
                    <div className="flex items-center gap-2">
                      <Mail className="w-3 h-3 text-slate-300" />{" "}
                      {client?.contact_email}
                    </div>
                  </div>
                </div>
                <div className="col-span-1 border-x border-slate-100 px-4">
                  <div className="text-[7pt] font-black text-slate-400 uppercase tracking-widest mb-2">
                    Ejecutivo
                  </div>
                  <div className="text-[sm] font-black leading-tight mb-0.5 brand-secondary">
                    {seller?.name}
                  </div>
                  <div className="text-[7pt] text-slate-500 mb-3">
                    {seller?.email}
                  </div>
                  <div className="text-[7pt] font-black text-slate-400 uppercase mb-0.5">
                    Validez
                  </div>
                  <div className="text-[7.5pt] font-black brand-secondary">
                    {displayValidityDate}
                  </div>
                </div>
                <div className="col-span-1 text-right">
                  <div className="text-[7pt] font-black text-slate-400 uppercase tracking-widest mb-2">
                    Resumen
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-[8pt]">
                      <span className="text-slate-500 font-bold">
                        Revisión:
                      </span>
                      <span className="font-black brand-secondary uppercase">
                        {versionLabel}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* TABLA CON DESCUENTOS INTEGRADOS */}
              <table className="w-full border-collapse mb-8">
                <thead className="table-header-compact">
                  <tr>
                    <th className="table-cell-compact text-left text-[7pt] font-black uppercase tracking-wider brand-secondary">
                      Servicio / Producto
                    </th>
                    <th className="table-cell-compact text-center text-[7pt] font-black uppercase tracking-wider brand-secondary">
                      Cant.
                    </th>
                    <th className="table-cell-compact text-right text-[7pt] font-black uppercase tracking-wider brand-secondary">
                      Precio Unit.
                    </th>
                    <th className="table-cell-compact text-right text-[7pt] font-black uppercase tracking-wider brand-secondary">
                      Extras
                    </th>
                    <th className="table-cell-compact text-right text-[7pt] font-black uppercase tracking-wider brand-secondary">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items &&
                    items.map((item, idx) => {
                      const currencySymbol = displayCompany.currency;

                      // Identificar reglas opcionales (no requeridas)
                      const optionalRules =
                        item.applied_rules?.filter(
                          (r: any) => !r.is_required,
                        ) || [];
                      const optionalExtrasTotal = optionalRules.reduce(
                        (acc: number, r: any) => {
                          return (
                            acc +
                            (parseFloat(
                              r.calculated_amount || r.price_modifier || "0",
                            ) || 0)
                          );
                        },
                        0,
                      );

                      // El precio unitario mostrado incluye el base + ajustes obligatorios
                      const displayUnitPrice =
                        (item.line_total - optionalExtrasTotal) /
                        (item.quantity || 1);
                      const unitPriceFormatted = `${currencySymbol}${displayUnitPrice.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

                      const extra = optionalExtrasTotal;
                      const extraFormatted =
                        Math.abs(extra) >= 0.01
                          ? `${extra > 0 ? "+" : ""}${currencySymbol}${Math.abs(extra).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          : "";

                      return (
                        <tr key={idx}>
                          <td className="table-cell-compact">
                            <div className="font-black text-slate-800 text-[9pt] leading-tight mb-1">
                              {item.description}
                            </div>
                          </td>
                          <td className="table-cell-compact text-center font-bold text-slate-600 text-xs">
                            {item.quantity}
                          </td>
                          <td className="table-cell-compact text-right">
                            <span className="font-black text-[9pt] brand-secondary">
                              {unitPriceFormatted}
                            </span>
                          </td>
                          <td className="table-cell-compact text-right">
                            <span className="font-black text-[9pt] brand-secondary">
                              {extraFormatted}
                            </span>
                          </td>
                          <td className="table-cell-compact text-right font-black text-[9.5pt] brand-secondary">
                            {`${currencySymbol}${(item.line_total || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>

              {/* TOTALES Y TÉRMINOS */}
              <div className="grid grid-cols-5 gap-10">
                <div className="col-span-3 space-y-5 pt-2">
                  <div>
                    <div className="text-[7pt] font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-2">
                      <div className="h-0.5 w-4 bg-slate-300" />
                      Observaciones
                    </div>
                    <div className="space-y-1">
                      {terms &&
                        terms.map((term, i) => (
                          <div
                            key={i}
                            className="flex gap-2 text-[7pt] text-slate-600 leading-tight"
                          >
                            <span className="font-black min-w-[10px] brand-secondary">
                              {i + 1}.
                            </span>
                            <p className="font-medium">{term}</p>
                          </div>
                        ))}
                    </div>
                  </div>
                  {quote.notes &&
                    quote.notes.replace(/\[DISCOUNT:.*?\]/g, "").trim() && (
                      <div className="text-[7.5pt] text-slate-600 italic bg-slate-50 p-3 border-l-2 border-slate-300">
                        <span className="font-black uppercase text-[6pt] not-italic block mb-0.5 brand-secondary">
                          Notas Generales:
                        </span>
                        {quote.notes.replace(/\[DISCOUNT:.*?\]/g, "").trim()}
                      </div>
                    )}
                </div>
                <div className="col-span-2 flex flex-col items-end pt-2">
                  <div className="w-full space-y-1.5 mb-2">
                    {(() => {
                      const netSubtotal =
                        items?.reduce(
                          (sum, item) => sum + (item.line_total || 0),
                          0,
                        ) || 0;
                      const extSub =
                        items?.reduce((sum, item) => {
                          const optionalRules =
                            item.applied_rules?.filter(
                              (r: any) => !r.is_required,
                            ) || [];
                          return (
                            sum +
                            optionalRules.reduce((acc: number, r: any) => {
                              return (
                                acc +
                                (parseFloat(
                                  r.calculated_amount ||
                                  r.price_modifier ||
                                  "0",
                                ) || 0)
                              );
                            }, 0)
                          );
                        }, 0) || 0;
                      const prodSub = netSubtotal - extSub;
                      const tax = totals?.tax > 0 ? totals.tax : 0;

                      let discountAmount = 0;
                      if (quote.notes) {
                        const discountMatch =
                          quote.notes.match(/\[DISCOUNT:({.*})\]/);
                        if (discountMatch) {
                          try {
                            const parsed = JSON.parse(discountMatch[1]);
                            const val = parseFloat(parsed.value) || 0;
                            if (parsed.type === "percentage") {
                              discountAmount = (netSubtotal * val) / 100;
                            } else {
                              discountAmount = val;
                            }
                          } catch (e) { }
                        }
                      }

                      const finalTotal =
                        Math.max(0, netSubtotal - discountAmount) + tax;
                      const currencySymbol = displayCompany.currency;
                      const format = (num: number) =>
                        `${currencySymbol}${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

                      return (
                        <>
                          <div className="flex justify-between text-[7.5pt] text-slate-500">
                            <span className="font-bold uppercase opacity-70">
                              Subtotal Productos
                            </span>
                            <span className="font-bold">{format(prodSub)}</span>
                          </div>
                          <div className="flex justify-between text-[7.5pt] text-slate-500">
                            <span className="font-bold uppercase opacity-70">
                              Ajustes / Extras
                            </span>
                            <span className="font-bold">{format(extSub)}</span>
                          </div>
                          <div className="flex justify-between text-[7.5pt] text-slate-600 font-bold border-t border-slate-100 pt-1">
                            <span className="uppercase">Subtotal Neto</span>
                            <span>{format(netSubtotal)}</span>
                          </div>
                          {discountAmount > 0 && (
                            <div className="flex justify-between text-[7.5pt] text-red-500">
                              <span className="font-bold uppercase opacity-70">
                                Descuento Global
                              </span>
                              <span className="font-bold">
                                - {format(discountAmount)}
                              </span>
                            </div>
                          )}
                          {tax > 0 && (
                            <div className="flex justify-between text-[7.5pt] text-slate-500">
                              <span className="font-bold uppercase opacity-70">
                                Impuestos
                              </span>
                              <span className="font-bold">{format(tax)}</span>
                            </div>
                          )}
                          <div className="bg-slate-100 border border-slate-200 px-4 py-2 rounded-md flex flex-col items-end w-full shadow-sm mt-2">
                            <span className="text-[7pt] font-black uppercase tracking-widest opacity-60 brand-secondary">
                              Total Final
                            </span>
                            <span className="text-xl font-black tracking-tighter brand-secondary">
                              {format(finalTotal)}
                            </span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* ANEXOS: NOTAS Y DETALLES DE PRODUCTOS */}
              {items &&
                items.some(
                  (item) =>
                    (item as any).notes?.trim() ||
                    item.applied_rules
                      ?.filter((r: any) => !r.is_required)
                      .some((r: any) => r.description || r.rule?.description),
                ) && (
                  <div className="mt-12" style={{ pageBreakInside: "avoid" }}>
                    <div className="bg-[#1e293b] text-white px-4 py-2 text-[8.5pt] font-black uppercase tracking-widest mb-6 rounded-sm">
                      Anexo: Detalles de Servicios y Notas
                    </div>
                    <div className="space-y-6">
                      {items
                        .filter(
                          (item) =>
                            (item as any).notes?.trim() ||
                            item.applied_rules
                              ?.filter((r: any) => !r.is_required)
                              .some(
                                (r: any) =>
                                  r.description || r.rule?.description,
                              ),
                        )
                        .map((item, idx) => {
                          const hasNotes = !!(item as any).notes?.trim();
                          const customRulesOnly =
                            item.applied_rules?.filter(
                              (r: any) => !r.is_required,
                            ) || [];
                          const hasServices = customRulesOnly.some(
                            (r: any) => r.description || r.rule?.description,
                          );

                          return (
                            <div
                              key={idx}
                              className="border border-slate-200 rounded-lg overflow-hidden break-inside-avoid shadow-sm print:shadow-none"
                            >
                              <div className="bg-slate-100 border-b border-slate-200 px-4 py-2.5">
                                <div className="font-black text-slate-800 text-[9pt] uppercase">
                                  {idx + 1}. {item.description}
                                </div>
                              </div>

                              <div className="p-4 grid grid-cols-2 gap-6 bg-white">
                                {hasServices && (
                                  <div
                                    className={
                                      hasNotes ? "col-span-1" : "col-span-2"
                                    }
                                  >
                                    <div className="text-[7pt] font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-2">
                                      <div className="h-px w-3 bg-slate-300" />
                                      Detalles del Servicio
                                    </div>
                                    <div className="space-y-2">
                                      {customRulesOnly.map(
                                        (ruleObj: any, rIdx: number) => {
                                          const rule = ruleObj.rule || ruleObj;
                                          if (!rule.description) return null;
                                          return (
                                            <div
                                              key={rIdx}
                                              className="bg-slate-50 p-2.5 rounded border border-slate-100"
                                            >
                                              <span className="text-[7.5pt] font-bold text-slate-700 block mb-0.5">
                                                {rule.rule_name || rule.name}:
                                              </span>
                                              <p className="text-[7.5pt] text-slate-600 italic">
                                                {rule.description}
                                              </p>
                                            </div>
                                          );
                                        },
                                      )}
                                    </div>
                                  </div>
                                )}

                                {hasNotes && (
                                  <div
                                    className={
                                      hasServices ? "col-span-1" : "col-span-2"
                                    }
                                  >
                                    <div className="text-[7pt] font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-2">
                                      <div className="h-px w-3 bg-slate-300" />
                                      Notas Adicionales
                                    </div>
                                    <div className="text-[8pt] text-slate-700 leading-relaxed italic bg-blue-50/40 p-3 rounded border border-blue-100">
                                      {(item as any).notes}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}

              {/* FOOTER - Firma y Sello */}
              <div className="mt-24 mb-8 flex flex-col items-start">
                <div className="w-64 border-t border-slate-800 mb-2"></div>
                <div className="text-[8pt] font-black text-slate-800">
                  Firma y sello de autorización del cliente
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AlertDialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <AlertDialogContent className="z-[10001]">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-indigo-600" />
              ¿Confirmar envío de cotización?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Se enviará este documento PDF al correo electrónico{" "}
              <span className="font-bold text-slate-900">
                {client?.contact_email}
              </span>
              . Asegúrate de que toda la información y los descuentos sean
              correctos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={async (e) => {
                e.preventDefault();
                if (onSendEmail) {
                  try {
                    const result = await onSendEmail();
                    if (result) {
                      setResultDialog({
                        isOpen: true,
                        type: result.success ? "success" : "error",
                        message: result.message,
                      });
                    }
                  } catch (err: any) {
                    setResultDialog({
                      isOpen: true,
                      type: "error",
                      message:
                        err.message ||
                        "Ocurrió un error al intentar enviar el correo.",
                    });
                  }
                  setIsConfirmOpen(false);
                }
              }}
              className="bg-slate-900 hover:bg-black text-white"
              disabled={isSending}
            >
              {isSending ? "Enviando..." : "Sí, enviar ahora"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={resultDialog.isOpen}
        onOpenChange={(open) =>
          !open && setResultDialog((prev) => ({ ...prev, isOpen: false }))
        }
      >
        <AlertDialogContent className="z-[10001]">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {resultDialog.type === "success" ? (
                <div className="bg-green-100 p-2 rounded-full text-green-600">
                  <MapPin className="w-5 h-5 hidden" />
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              ) : (
                <div className="bg-red-100 p-2 rounded-full text-red-600">
                  <AlertCircle className="w-5 h-5" />
                </div>
              )}
              {resultDialog.type === "success"
                ? "Envío Exitoso"
                : "Error al Enviar"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base mt-2">
              {resultDialog.message}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() =>
                setResultDialog((prev) => ({ ...prev, isOpen: false }))
              }
              className={
                resultDialog.type === "success"
                  ? "bg-slate-900 hover:bg-black text-white"
                  : "bg-slate-900 hover:bg-slate-800 text-white"
              }
            >
              Entendido
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>,
    document.body,
  );
};
